import { useCallback, useEffect, useState } from 'react';
import { api } from './api.js';
import type { ÉtatSession } from './api.js';
import { Connexion } from './pages/Connexion.js';
import { EnrolementTotp } from './pages/EnrolementTotp.js';
import { Flotte } from './pages/Flotte.js';
import { Onboarding } from './pages/Onboarding.js';
import { Réglages } from './pages/Reglages.js';

type Vue = 'chargement' | 'connexion' | 'enrolement' | 'application';

/**
 * Aiguillage de l'application.
 *
 * La vue à afficher est déduite de l'état renvoyé par le serveur, jamais d'un état conservé côté
 * navigateur : c'est le serveur qui sait si la session est complète, en attente de second facteur,
 * ou en cours d'enrôlement. Le frontend ne fait que refléter cette réponse.
 */
export function App() {
  const [vue, setVue] = useState<Vue>('chargement');
  const [session, setSession] = useState<ÉtatSession | null>(null);
  const [onboardingPour, setOnboardingPour] = useState<string | null>(null);
  const [réglagesPour, setRéglagesPour] = useState<{ id: string; rôle: string } | null>(null);

  const appliquer = useCallback((état: ÉtatSession, status?: number) => {
    setSession(état);
    if (état.user !== undefined) {
      setVue('application');
      return;
    }
    if (état.enrollmentRequired === true) {
      setVue('enrolement');
      return;
    }
    setVue('connexion');
    if (status !== undefined && status >= 500) {
      setSession({ error: 'Service indisponible' });
    }
  }, []);

  useEffect(() => {
    let annulé = false;
    void (async () => {
      try {
        const { status, data } = await api.session();
        if (!annulé) appliquer(data, status);
      } catch {
        if (!annulé) {
          setSession({ error: 'Service injoignable' });
          setVue('connexion');
        }
      }
    })();
    return () => {
      annulé = true;
    };
  }, [appliquer]);

  async function déconnecter() {
    await api.déconnexion();
    setSession(null);
    setVue('connexion');
  }

  if (vue === 'chargement') {
    return (
      <main className="coquille coquille--étroite">
        <p className="aide">Chargement…</p>
      </main>
    );
  }

  if (vue === 'connexion') {
    return (
      <>
        {session?.error !== undefined && (
          <p className="bandeau-erreur" role="alert">
            {session.error}
          </p>
        )}
        <Connexion onSession={(état) => appliquer(état)} />
      </>
    );
  }

  if (vue === 'enrolement') {
    return <EnrolementTotp onSession={(état) => appliquer(état)} />;
  }

  const utilisateur = session?.user;
  // Une seule organisation à l'affichage pour l'instant ; le sélecteur viendra quand le parc
  // couvrira plusieurs médias par compte.
  const organisationCourante = utilisateur?.memberships[0];
  return (
    <main className="coquille">
      <header className="entete">
        <div>
          <h1>Vigie</h1>
          <p className="sous-titre">Gestion du parc Ghost — Médianes</p>
        </div>
        <div className="entete-compte">
          <span className="courriel">{utilisateur?.email}</span>
          <button type="button" className="lien" onClick={() => void déconnecter()}>
            Se déconnecter
          </button>
        </div>
      </header>

      {réglagesPour !== null ? (
        <Réglages
          organizationId={réglagesPour.id}
          rôle={réglagesPour.rôle}
          onRetour={() => setRéglagesPour(null)}
        />
      ) : onboardingPour !== null ? (
        <Onboarding organizationId={onboardingPour} onTerminé={() => setOnboardingPour(null)} />
      ) : (
        <section className="carte">
          <h2>Organisations</h2>
          {utilisateur !== undefined && utilisateur.memberships.length > 0 ? (
            <ul className="liste-organisations">
              {utilisateur.memberships.map((appartenance) => (
                <li key={appartenance.organizationId}>
                  <span>{appartenance.organizationName}</span>
                  <span className={`role role--${appartenance.role}`}>{appartenance.role}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="aide">Aucune organisation associée à ce compte.</p>
          )}
          {utilisateur !== undefined &&
            utilisateur.memberships.some((appartenance) => appartenance.role === 'admin') && (
              <div className="actions">
                <button
                  type="button"
                  onClick={() =>
                    setOnboardingPour(
                      utilisateur.memberships.find((a) => a.role === 'admin')!.organizationId,
                    )
                  }
                >
                  Ajouter un site
                </button>
                {organisationCourante !== undefined && (
                  <button
                    type="button"
                    className="lien"
                    onClick={() =>
                      setRéglagesPour({
                        id: organisationCourante.organizationId,
                        rôle: organisationCourante.role,
                      })
                    }
                  >
                    Réglages de l’organisation
                  </button>
                )}
              </div>
            )}
        </section>
      )}

      {onboardingPour === null && réglagesPour === null && organisationCourante !== undefined && (
        <section className="section-flotte">
          <h2>Flotte</h2>
          <Flotte
            organizationId={organisationCourante.organizationId}
            rôle={organisationCourante.role}
          />
        </section>
      )}
    </main>
  );
}
